import React, { ReactNode } from 'react';
import { AlertCircle, AlertTriangle, CheckCircle, Info } from 'lucide-react';
import { Typography } from '@crisellevaldez/speculo-ui';

export type AlertVariant = 'info' | 'warning' | 'error' | 'success';

interface AlertProps {
  variant?: AlertVariant;
  children: ReactNode;
  className?: string;
  showIcon?: boolean;
}

export const Alert: React.FC<AlertProps> = ({
  variant = 'info',
  children,
  className = '',
  showIcon = true,
}) => {
  const getVariantStyles = (): {
    bg: string;
    borderLeft: string;
    text: string;
    icon: ReactNode;
  } => {
    switch (variant) {
      case 'info':
        return {
          bg: 'bg-blue-50 dark:bg-blue-900/20',
          borderLeft: 'border-blue-400 dark:border-blue-600',
          text: 'text-blue-800 dark:text-blue-300',
          icon: <Info className="w-5 h-5 text-blue-500 dark:text-blue-400" />,
        };
      case 'warning':
        return {
          bg: 'bg-amber-50 dark:bg-amber-900/20',
          borderLeft: 'border-amber-400 dark:border-amber-600',
          text: 'text-amber-800 dark:text-amber-300',
          icon: <AlertTriangle className="w-5 h-5 text-amber-500 dark:text-amber-400" />,
        };
      case 'error':
        return {
          bg: 'bg-red-50 dark:bg-red-900/20',
          borderLeft: 'border-red-400 dark:border-red-600',
          text: 'text-red-800 dark:text-red-300',
          icon: <AlertCircle className="w-5 h-5 text-red-500 dark:text-red-400" />,
        };
      case 'success':
        return {
          bg: 'bg-green-50 dark:bg-green-900/20',
          borderLeft: 'border-green-400 dark:border-green-600',
          text: 'text-green-800 dark:text-green-300',
          icon: <CheckCircle className="w-5 h-5 text-green-500 dark:text-green-400" />,
        };
      default:
        return {
          bg: 'bg-blue-50 dark:bg-blue-900/20',
          borderLeft: 'border-blue-400 dark:border-blue-600',
          text: 'text-blue-800 dark:text-blue-300',
          icon: <Info className="w-5 h-5 text-blue-500 dark:text-blue-400" />,
        };
    }
  };

  const styles = getVariantStyles();

  return (
    <div className={`${styles.bg} border-l-4 ${styles.borderLeft} p-4 rounded ${className}`}>
      <div className={`flex ${showIcon ? 'items-center gap-3' : ''}`}>
        {showIcon && styles.icon}
        <Typography variant="small" className={styles.text}>
          {children}
        </Typography>
      </div>
    </div>
  );
};

export default Alert;
